import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileBarChart, FolderKanban, Users, LogOut, Menu, X, Settings, PieChart as PieChartIcon, Check, XCircle } from "lucide-react"; // Added Check, XCircle
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

const NAV = [
  { to: "/tracker", label: "Tracker", icon: LayoutDashboard, group: "TRACK" },
  { to: "/dashboard", label: "Dashboard", icon: PieChartIcon, group: "ANALYZE" },
  { to: "/reports", label: "Reports", icon: FileBarChart, group: "ANALYZE" },
  { to: "/projects", label: "Projects", icon: FolderKanban, group: "MANAGE" },
  { to: "/team", label: "Team", icon: Users, group: "MANAGE" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
 // --- NEW: Name Update States ---
  const [showNameModal, setShowNameModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  // --------------------------------
  
  const handleLogout = () => { logout(); navigate("/login"); };
  // ✅ ADDED THIS LINE: Filter out MANAGE items if the user is not an admin
  const filteredNav = user?.role === "admin" ? NAV : NAV.filter(item => item.group !== "MANAGE");

  // Group nav by group (Changed NAV to filteredNav here)
  const grouped = filteredNav.reduce((acc, item) => {
  // Group nav by group
  //const grouped = NAV.reduce((acc, item) => {
    acc[item.group] = acc[item.group] || [];
    acc[item.group].push(item);
    return acc;
  }, {});
// --- NEW: Handle Name Save ---
    const handleSaveName = async () => {
    if (!newName.trim()) return;
    setIsSavingName(true);

    try {
      // Ensure the API URL is set. Fallback to localhost:8000 for local dev if missing.
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const res = await fetch(`${API_URL}/api/users/me`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('ess_token')}` 
        },
        body: JSON.stringify({ name: newName.trim() })
      });

      // ADD THIS CHECK: Ensure the response is actually JSON, not HTML!
      const contentType = res.headers.get('content-type');
      if (!res.ok || !contentType || !contentType.includes('application/json')) {
        throw new Error("Failed to connect to backend API");
      }

      setShowNameModal(false);
      window.location.reload(); 
    } catch (error) {
      alert("Network error: " + error.message);
    } finally {
      setIsSavingName(false);
    }
  };
  // --------------------------------
  return (
    <div className="flex min-h-screen bg-white">
      {/* Mobile menu trigger */}
      <button
        className="lg:hidden fixed top-3 left-3 z-50 p-2 bg-white border border-gray-200 shadow-sm"
        onClick={() => setOpen(true)}
        data-testid="mobile-menu-btn"
      >
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 border-r border-gray-200 bg-[var(--ess-surface)] flex-shrink-0 flex flex-col z-40 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="px-6 h-20 flex items-center justify-between border-b border-gray-200">
          <Logo size={28} />
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-6">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="mb-6">
              <div className="px-6 mb-2 ess-section-label">{group}</div>
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                      isActive
                        ? "border-[var(--ess-ink)] bg-white text-[var(--ess-ink)]"
                        : "border-transparent text-gray-600 hover:bg-white hover:text-[var(--ess-ink)]"
                    }`
                  }
                  data-testid={`nav-${label.toLowerCase()}`}
                >
                  <Icon size={16} strokeWidth={1.75} />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}

          {user?.role === "admin" && (
            <div className="mb-6">
              <div className="px-6 mb-2 ess-section-label">ADMIN</div>
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-colors border-l-2 ${
                    isActive
                      ? "border-[var(--ess-ink)] bg-white text-[var(--ess-ink)]"
                      : "border-transparent text-gray-600 hover:bg-white hover:text-[var(--ess-ink)]"
                  }`
                }
                data-testid="nav-admin"
              >
                <Settings size={16} strokeWidth={1.75} />
                Settings
              </NavLink>
            </div>
          )}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <div className="mb-3">
            <div 
              className="text-sm font-medium text-[var(--ess-ink)] truncate cursor-pointer hover:underline" 
              data-testid="user-name"
              title="Click to edit name"
              onClick={() => {
                setNewName(user?.name || "");
                setShowNameModal(true);
              }}
            >
              {user?.name}
            </div>
            <div className="text-xs text-gray-500 truncate" data-testid="user-email">{user?.email}</div>
            <div className="ess-section-label mt-1" data-testid="user-role">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600 hover:text-[var(--ess-ink)]"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/30 z-30" onClick={() => setOpen(false)} />}
      {/* --- NEW: Name Update Modal --- */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-80 border border-gray-200">
            <h3 className="text-lg font-semibold text-[var(--ess-ink)] mb-4">Update Name</h3>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ess-ink)]"
              placeholder="Enter new name"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
            />
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowNameModal(false)} className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
                <XCircle size={14} /> Cancel
              </button>
              <button onClick={handleSaveName} disabled={isSavingName} className="flex items-center gap-1 px-4 py-2 text-sm text-white bg-[var(--ess-ink)] rounded hover:opacity-90 disabled:opacity-50">
                <Check size={14} /> {isSavingName ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* --------------------------------- */}
      <main className="flex-1 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
